import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Section } from "./shared";

/* ==========================
   GALLERY PRO STYLED COMPONENTS
========================== */
const GallerySection = styled(Section)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  min-height: 424px;
  height: 100%;

  @media (max-width: 768px) {
    min-height: auto;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 110px;
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MainStage = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 247, 210, 0.25);
  background: radial-gradient(900px 240px at 10% 0%, rgba(0, 247, 210, 0.18), transparent 55%),
    linear-gradient(180deg, rgba(13, 18, 32, 0.92), rgba(13, 18, 32, 0.75));
  box-shadow: 0 0 22px rgba(0, 247, 210, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainStageInner = styled.button`
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainImage = styled.img`
  width: 100%;
  height: 380px;
  object-fit: contain;
  object-position: center;
  display: block;
  background: rgba(13, 18, 32, 0.3);

  @media (max-width: 900px) {
    height: 320px;
  }
`;

const StageTopRow = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  pointer-events: none;
  z-index: 10;
`;

const StageChip = styled.div`
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(31, 41, 55, 0.95);
  background: rgba(11, 14, 20, 0.55);
  color: #e5e7eb;
  backdrop-filter: blur(8px);
`;

const ChipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00f7d2;
  box-shadow: 0 0 10px rgba(0, 247, 210, 0.55);
`;

const StageActions = styled.div`
  pointer-events: auto;
  display: flex;
  gap: 8px;
`;

const IconBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid #1f2937;
  background: rgba(13, 18, 32, 0.55);
  color: #e5e7eb;
  cursor: pointer;
  font-weight: 900;
  box-shadow: 0 0 0 rgba(0, 247, 210, 0);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 18px rgba(0, 247, 210, 0.14);
  }
`;

const ThumbsRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 380px;
  overflow: auto;
  padding-right: 2px;

  @media (max-width: 900px) {
    flex-direction: row;
    max-height: none;
    overflow: auto;
    padding-right: 0;
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(31, 41, 55, 0.9);
    border-radius: 999px;
  }
`;

const ThumbBtn = styled.button`
  width: 96px;
  height: 72px;
  border-radius: 14px;
  border: 1px solid #1f2937;
  padding: 0;
  background: #0d1220;
  overflow: hidden;
  cursor: pointer;
  opacity: ${({ active }) => (active ? 1 : 0.92)};
  transition: transform 0.15s ease, opacity 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  border-color: ${({ active }) => (active ? 'rgba(0, 247, 210, 0.55)' : '#1f2937')};
  box-shadow: ${({ active }) => (active ? '0 0 18px rgba(0, 247, 210, 0.14)' : 'none')};

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
  }
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.86);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const LightboxFrame = styled.div`
  width: min(1100px, 92vw);
  height: min(720px, 86vh);
  border-radius: 18px;
  border: 1px solid rgba(31, 41, 55, 0.95);
  background: radial-gradient(900px 240px at 10% 0%, rgba(0, 247, 210, 0.12), transparent 55%),
    rgba(13, 18, 32, 0.92);
  box-shadow: 0 0 32px rgba(0, 247, 210, 0.12);
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto;
`;

const LbTop = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const LbTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 900;
  color: #e5e7eb;
`;

const LbControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const LbStage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const LbImage = styled.img`
  max-width: 92%;
  max-height: 92%;
  object-fit: contain;
  object-position: center;
  border-radius: 16px;
  user-select: none;
  -webkit-user-drag: none;
  background: rgba(13, 18, 32, 0.3);
`;

const LbArrow = styled.button`
  position: absolute;
  top: 50%;
  ${({ side }) => (side === 'l' ? 'left: 12px;' : 'right: 12px;')}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 14px;
  border: 1px solid #1f2937;
  background: rgba(11, 14, 20, 0.55);
  color: #e5e7eb;
  cursor: pointer;
  font-weight: 900;
  box-shadow: 0 0 18px rgba(0, 247, 210, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 0 24px rgba(0, 247, 210, 0.2);
  }
`;

const LbThumbs = styled.div`
  padding: 12px;
  display: flex;
  gap: 10px;
  overflow: auto;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(31, 41, 55, 0.9);
    border-radius: 999px;
  }
`;

function GalleryPRO({ images }) {
  const safeImages = Array.isArray(images) && images.length ? images : [];
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  const activeImg = safeImages[index] || "";

  const openLb = (i) => {
    if (!safeImages.length) return;
    setIndex(Math.max(0, Math.min(safeImages.length - 1, i)));
    setZoom(1);
    setRotation(0);
    setLightbox(true);
  };

  const closeLb = () => {
    setLightbox(false);
    setZoom(1);
    setRotation(0);
  };

  const next = () => {
    if (!safeImages.length) return;
    setIndex((i) => (i + 1) % safeImages.length);
    setZoom(1);
    setRotation(0);
  };

  const prev = () => {
    if (!safeImages.length) return;
    setIndex((i) => (i - 1 + safeImages.length) % safeImages.length);
    setZoom(1);
    setRotation(0);
  };

  const zoomIn = () => setZoom((z) => Math.min(3, Math.max(1, z + 0.2)));
  const zoomOut = () => setZoom((z) => Math.min(3, Math.max(1, z - 0.2)));
  const resetZoom = () => {
    setZoom(1);
    setRotation(0);
  };
  const rotate = () => setRotation((r) => (r + 90) % 360);

  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLb();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "+" || e.key === "=") zoomIn();
      if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, safeImages.length]);

  const handleWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY > 0 ? -0.12 : 0.12;
    setZoom((z) => Math.min(3, Math.max(1, z + direction)));
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx > 40) prev();
    if (dx < -40) next();
    setTouchStartX(null);
  };

  return (
    <GallerySection>
      <h3 style={{ marginTop: 0 }}>Product Gallery PRO</h3>

      {!safeImages.length ? (
        <p style={{ opacity: 0.75 }}>No images.</p>
      ) : (
        <GalleryGrid>
          <MainStage>
            <StageTopRow>
              <StageChip>
                <ChipDot />
                <span style={{ fontWeight: 900 }}>Preview</span>
                <span style={{ opacity: 0.75 }}>
                  {index + 1}/{safeImages.length}
                </span>
              </StageChip>
              <StageActions>
                <IconBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    openLb(index);
                  }}
                  title="Fullscreen"
                >
                  ⤢
                </IconBtn>
              </StageActions>
            </StageTopRow>

            <MainStageInner onClick={() => openLb(index)} aria-label="Open gallery">
              <MainImage src={activeImg} alt="product" loading="lazy" />
            </MainStageInner>
          </MainStage>

          <ThumbsRail>
            {safeImages.map((img, i) => (
              <ThumbBtn
                key={img + i}
                active={i === index}
                onClick={() => setIndex(i)}
                aria-label={`Image ${i + 1}`}
              >
                <ThumbImg src={img} alt={`thumb-${i + 1}`} loading="lazy" />
              </ThumbBtn>
            ))}
          </ThumbsRail>
        </GalleryGrid>
      )}

      {lightbox && safeImages.length > 0 && (
        <LightboxOverlay
          onClick={closeLb}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <LightboxFrame onClick={(e) => e.stopPropagation()}>
            <LbTop>
              <LbTitle>
                <span style={{ color: "#00f7d2" }}>●</span>
                Gallery
                <span style={{ opacity: 0.7, fontSize: 12 }}>
                  {index + 1}/{safeImages.length}
                </span>
              </LbTitle>
              <LbControls>
                <IconBtn onClick={zoomOut} title="Zoom out">−</IconBtn>
                <IconBtn onClick={zoomIn} title="Zoom in">+</IconBtn>
                <IconBtn onClick={resetZoom} title="Reset zoom">↺</IconBtn>
                <IconBtn onClick={prev} title="Prev">‹</IconBtn>
                <IconBtn onClick={next} title="Next">›</IconBtn>
                <IconBtn onClick={closeLb} title="Close">✕</IconBtn>
              </LbControls>
            </LbTop>

            <LbStage>
              <LbArrow side="l" onClick={prev} aria-label="Previous">‹</LbArrow>
              <LbImage
                src={activeImg}
                alt="product-full"
                style={{ transform: `scale(${zoom})`, transition: "transform .12s ease-out" }}
                onDoubleClick={() => setZoom((z) => (z === 1 ? 1.8 : 1))}
              />
              <LbArrow side="r" onClick={next} aria-label="Next">›</LbArrow>
            </LbStage>

            <LbThumbs>
              {safeImages.map((img, i) => (
                <ThumbBtn
                  key={`lb-${img}-${i}`}
                  active={i === index}
                  onClick={() => {
                    setIndex(i);
                    setZoom(1);
                  }}
                  style={{ width: 88, height: 64, flex: "0 0 auto" }}
                >
                  <ThumbImg src={img} alt={`lb-thumb-${i + 1}`} />
                </ThumbBtn>
              ))}
            </LbThumbs>
          </LightboxFrame>
        </LightboxOverlay>
      )}
    </GallerySection>
  );
}

export default GalleryPRO;
